import type { ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ProGateSkeleton, CatalogueProGate, AccessGate } from '@/components/ui/pro-gate'
import { useAuth } from '@/hooks/useAuth'
import { useSubscription } from '@/hooks/useSubscription'
import { useIsOperator } from '@/hooks/useIsOperator'
import { useDestinationAccess } from '@/hooks/useDestinationAccess'
import { useOffersModule } from '@/hooks/useOffersModule'
import { ErrorState } from '@/components/ui/error-state'
import { canUsePresentations } from '@/lib/presentations-access'
import { isEventId } from '@/lib/event-links'
import { NotFound } from '@/pages/NotFound'

/**
 * Rutevaktene, samlet.
 *
 * De bodde i `routes/index.tsx`, som også eksporterer ruteren — og en fil som
 * eksporterer både komponenter og annet mister fast refresh, som er hele
 * `react-refresh/only-export-components`. Å legge en fjerde vakt der ville
 * vokst den gjelden med én til; å flytte alle tre ut betaler ned tre.
 */

/**
 * F-08 · en `:id` som ikke KAN være et arrangement, får 404 — ikke en datafeil.
 *
 * `/event/create` traff `/event/:id`, som spurte databasen om et arrangement med
 * id-en «create» og tegnet «Noe gikk galt · Prøv igjen». Leseren fikk altså en
 * prøv-igjen-knapp på et utfall som aldri endrer seg, og det er nøyaktig det
 * `EventDetailPage` sin egen kommentar sier at et ikke-funnet aldri skal tilby.
 *
 * Vakten står på ruten og ikke inne i siden, av to grunner: den dekker alle fire
 * `/event/:id*`-rutene med én forekomst, og den kjører FØR fetchen, så en
 * ugyldig adresse koster null nettverkskall.
 *
 * Formen er sannheten (`isEventId`), ikke en liste over reserverte ord. Se
 * `lib/event-links.ts` for hvorfor.
 */
export function RequireEventId({ children }: { children: ReactNode }) {
  const { id } = useParams<{ id: string }>()
  if (!isEventId(id)) return <NotFound />
  return <>{children}</>
}

/** Presentasjonsverktøyet er internt — begrenset til en liste med kontoer. */
export function RequirePresentationsAccess({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  if (!canUsePresentations(user?.email)) return <AccessGate tool="presentations" />
  return <>{children}</>
}

/**
 * Operatørflatene: `/kommunikasjon`, `/offers` og `/catalogue`.
 *
 * De sto åpne for enhver innlogget konto, altså også for hver deltaker på en
 * kundes arrangement. Å skjule menyoppføringen er ikke nok — URL-en er
 * offentlig kunnskap, og /kommunikasjon leser kontaktregisteret. Dette er
 * porten; menyen er bare høfligheten.
 *
 * `loading` respekteres av samme grunn som i `RequireProAccess` under: å
 * redirigere på `false` før svaret er inne kaster ut en operatør som bare var
 * treg på nett. Skjelett, ikke blank skjerm, og ikke en feilaktig utkastelse.
 *
 * Ingen omdirigering (03.10.2026): `AccessGate` sier på stedet hva flaten er og
 * hvorfor den er stengt, og peker på arrangementene hennes. En stille
 * omdirigering så ut som en ødelagt lenke.
 */
export function RequireOperator({ children, tool = 'communication' }: { children: ReactNode; tool?: 'communication' | 'offersRole' }) {
  const { isOperator, loading } = useIsOperator()
  if (loading) return <ProGateSkeleton />
  // The gate names the surface that was asked for: offers said «Kommunikasjon er for …» until 25.10.2026.
  if (!isOperator) return <AccessGate tool={tool} />
  return <>{children}</>
}

/**
 * Tilbudsmodulen er av for aktiv organisasjon (Innstillinger → Organisasjon). Samme kilde som
 * menyen, så en direkte URL til /offers eller /catalogue ender på /events i stedet for en side
 * organisasjonen ikke har.
 */
export function RequireOffersModule({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { enabled, loading } = useOffersModule()
  if (loading) return <ProGateSkeleton />
  // Inside RequireOperator, so the viewer administers the organisation: the button goes to the switch.
  if (!enabled) return <AccessGate tool="offers" action={{ to: '/settings/organization', label: t('accessGate.offers.action') }} />
  return <>{children}</>
}

/** Site delegates use their existing destination access, independently of operator tools. */
export function RequireDestinationAccess({ children }: { children: ReactNode }) {
  const { canAccessDestination, loading, error, retry } = useDestinationAccess()
  if (loading) return <ProGateSkeleton />
  if (error) return <ErrorState onRetry={retry} />
  if (!canAccessDestination) return <AccessGate tool="destinations" />
  return <>{children}</>
}

/**
 * Pro-flatene (leverandørkatalogen). Venter på at nivået skal løses opp, så en
 * Pro-via-org-bruker ikke blir kastet ut mens org-oppslaget pågår.
 */
export function RequireProAccess({ children }: { children: ReactNode }) {
  const { isPro, loading } = useSubscription()
  // 21d: «/catalogue trenger TO tilstander, ikke én.» Vakten venter med vilje
  // på nivået — behold det — men den returnerte `null` i mellomtiden, altså en
  // blank skjerm. «Skjelett, ikke blank skjerm.»
  if (loading) return <ProGateSkeleton />
  // 21d's andre tilstand, og det ER en tilstand, ikke en redirect:
  // «Leverandørkatalogen er i Pro · Søk i hoteller, transport og leverandører du
  // har brukt før · Se Pro · Tilbake». Fase 8 hadde allerede byttet
  // bounce-to-dashboard med en bounce til /pricing (22b: «det er slik en
  // oppgradering selges, ikke ved å skjule den»), som var riktig retning — men
  // en redirect kaster fortsatt bort det brukeren ba om. Å navngi funksjonen på
  // stedet og peke på Pro selger den; å bli flyttet et annet sted leses som at
  // siden er ødelagt.
  if (!isPro) return <CatalogueProGate />
  return <>{children}</>
}
